/**
 * §8's "recording watched state", as a pure decision: *has this playback earned a
 * `POST /api/v1/auth/history/{id}`?*
 *
 * §8 fixes the threshold — "fired once per video after a meaningful watch threshold (~30 s or 25 %,
 * whichever first), debounced, and never retried on 403". This module is the "whichever first" part and
 * nothing else; the once-per-video and the never-retry live with the caller, because both are about a
 * request's lifetime rather than about a position.
 *
 * It is in core rather than in the Watch screen for R13's usual reason and one specific one: an Android
 * player reports position through Media3 rather than through a `<video>` element, and "when do we call
 * this a watch" must not be a number buried in a web-only effect.
 */

/** §8's absolute threshold: half a minute is a watch even on a three-hour video. */
export const WATCHED_THRESHOLD_SEC = 30;

/** §8's relative threshold, which is what makes a 40-second Short recordable at all. */
export const WATCHED_THRESHOLD_FRACTION = 0.25;

export interface WatchedProgress {
  /** Where the player is now, in seconds. */
  readonly positionSec: number;
  /**
   * The video's length, or `null` when there isn't one — a live stream, or a plan whose duration has not
   * arrived yet. `null` is *not* treated as zero: it disables the relative arm and leaves the absolute
   * one, so a live stream is recorded after 30 seconds and never on the fraction of an unknown whole.
   */
  readonly durationSec: number | null;
}

/**
 * Whether this position is far enough in to call it watched.
 *
 * **"Whichever first" is a disjunction, and on short videos the fraction is what fires.** A 40-second
 * Short can never reach 30 seconds of playback without being over; 25 % of it is 10 seconds. Conversely a
 * three-hour stream reaches 30 seconds long before 25 %, and waiting for 45 minutes would record almost
 * nothing. Neither arm alone covers the corpus this app actually plays — Shorts are `shortVideo` records
 * throughout the feeds, and 24/7 radios report 866 days of length.
 *
 * A seek does not launder the requirement: this reads the *current* position, so dragging the bar to 90 %
 * is a watch by any reading of the word, which is also how YouTube behaves. That is deliberate — the
 * alternative is accumulating played time, which needs state, a definition of "played" and a decision
 * about whether a 2× pass counts, none of which §8 asks for and all of which would be invented here.
 *
 * Guards on non-finite input rather than trusting the player: a `<video>` reports `NaN` duration before
 * metadata loads and `Infinity` on a live stream, and both would make a naive comparison record every
 * video the moment it was opened.
 */
export function shouldRecordWatched(progress: WatchedProgress): boolean {
  const { positionSec, durationSec } = progress;
  if (!Number.isFinite(positionSec) || positionSec <= 0) return false;

  if (positionSec >= WATCHED_THRESHOLD_SEC) return true;

  if (durationSec === null || !Number.isFinite(durationSec) || durationSec <= 0) return false;
  return positionSec >= durationSec * WATCHED_THRESHOLD_FRACTION;
}
